import { store } from "@/store";
import {
  ESidebarMenuType,
  setSideBarMenuTypePopupDisplay,
  toggleDatasetExplorerPopup
} from "@/store/slice/uiSlice";

const onboardingDatasetCardSelector = '[data-tourid="dataset-card-explorer"]';
const onboardingAddDatasetButtonSelector = '[data-tourid="add-dataset-btn"]';

export const openDatasetExplorer = () => {
  store.dispatch(toggleDatasetExplorerPopup(true));
};

const clickOnboardingDatasetCard = () => {
  const datasetCard = document.querySelector<HTMLElement>(
    onboardingDatasetCardSelector
  );

  datasetCard?.click();
  return Boolean(datasetCard);
};

export const selectOnboardingDataset = () => {
  clickOnboardingDatasetCard();
};

/**
 * Auto-selects the onboarding dataset card, clicking it as soon as it exists.
 *
 * The card (`data-tourid="dataset-card-explorer"`) is rendered asynchronously:
 * DatasetExplorerPopup streams its data, so the element is usually NOT in the
 * DOM at the moment this step activates. A single immediate click would race
 * the render, miss, and leave the tour stuck with no dataset selected.
 *
 * Strategy:
 *  1. Try clicking now — if the card already exists, we're done.
 *  2. Otherwise watch `document.body` with a MutationObserver and retry the
 *     click on every mutation until the card appears, then disconnect.
 *  3. A 5s timeout disconnects the observer as a safety valve so it never
 *     watches the DOM indefinitely.
 *
 * @returns a teardown function that clears the timeout and disconnects the
 * observer. The tour calls this if the step unmounts before the card shows,
 * preventing a leaked observer and a stale click on a later screen.
 */
const selectOnboardingDatasetWhenReady = () => {
  if (clickOnboardingDatasetCard()) return;

  const observer = new MutationObserver(() => {
    if (!clickOnboardingDatasetCard()) return;

    observer.disconnect();
  });

  const timeoutId = window.setTimeout(() => {
    observer.disconnect();
  }, 5000);

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  return () => {
    window.clearTimeout(timeoutId);
    observer.disconnect();
  };
};

export const openDatasetExplorerAndSelectDatasetWhenReady = () => {
  openDatasetExplorer();
  return selectOnboardingDatasetWhenReady();
};

export const clickAddDatasetButton = () => {
  document
    .querySelector<HTMLElement>(onboardingAddDatasetButtonSelector)
    ?.click();
};

export const openSpatialAnalysis = () => {
  store.dispatch(toggleDatasetExplorerPopup(false));
  store.dispatch(setSideBarMenuTypePopupDisplay(ESidebarMenuType.SPATIAL));
};

export const getExploreDatasetPreSelectionStepIds = (
  steps: readonly { tourId: string }[],
  selectionTourId: string
) => {
  const selectionIndex = steps.findIndex(
    (step) => step.tourId === selectionTourId
  );

  return selectionIndex < 0
    ? []
    : steps.slice(0, selectionIndex).map((step) => step.tourId);
};
